import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Sparkles } from 'lucide-react'

import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DynamicIslandDemo } from '@/features/dynamic-island/dynamic-island-demo'
import { DynamicIslandV2 } from '@/features/dynamic-island/dynamic-island-v2'
import { DynamicIslandV3 } from '@/features/dynamic-island/dynamic-island-v3'
import { DynamicIslandV4 } from '@/features/dynamic-island/dynamic-island-v4'
import { DynamicIslandV5 } from '@/features/dynamic-island/dynamic-island-v5'
import { DynamicIslandV6 } from '@/features/dynamic-island/dynamic-island-v6'
import { requireAuth } from '@/lib/auth'

export const Route = createFileRoute('/_dashboard/dynamic-island')({
  beforeLoad: requireAuth(),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: DynamicIslandPage,
})

function DynamicIslandPage() {
  const [version, setVersion] = useState<1 | 2 | 3 | 4 | 5 | 6>(6)

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <PageBreadcrumb />
          <h1 className="mt-2 text-2xl font-bold tracking-tight">Dynamic Island</h1>
          <p className="text-sm text-muted-foreground">
            Apple-style island toast demo — fires at the top center of the screen
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border p-1">
          <Button size="sm" variant={version === 1 ? 'secondary' : 'ghost'} onClick={() => setVersion(1)}>
            Version 1
          </Button>
          <Button size="sm" variant={version === 2 ? 'secondary' : 'ghost'} onClick={() => setVersion(2)}>
            Version 2
          </Button>
          <Button size="sm" variant={version === 3 ? 'secondary' : 'ghost'} onClick={() => setVersion(3)}>
            Version 3
          </Button>
          <Button size="sm" variant={version === 4 ? 'secondary' : 'ghost'} onClick={() => setVersion(4)}>
            Version 4
          </Button>
          <Button size="sm" variant={version === 5 ? 'secondary' : 'ghost'} onClick={() => setVersion(5)}>
            Version 5
          </Button>
          <Button size="sm" variant={version === 6 ? 'secondary' : 'ghost'} onClick={() => setVersion(6)}>
            Version 6
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="size-4" /> Dynamic Island · V{version}
          </CardTitle>
          <CardDescription>
            {version === 6
              ? 'Receipt ink: the Version 11 reports skin — green charcoal, one mint accent, mono data, hairline dividers and status words. No glow, all spring.'
              : version === 5
              ? "V4's spring engine with luminous typography — titles and values carry neon glows matching the icon halos."
              : version === 4
              ? 'The smooth one: frame-rate-independent 240Hz physics, glide-tuned springs, every fade driven off the spring itself.'
              : version === 3
                ? 'Physically sprung: real spring dynamics, measured auto-height, squeeze-out replacement, action buttons, arrival pop.'
              : version === 2
                ? 'The full treatment: blur cross-fades, live two-stage sync activity, per-type trailing values, click to dismiss.'
                : 'Pops in as a capsule, springs open with the notification, holds, then shrinks away.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {version === 6 ? <DynamicIslandV6 /> : version === 5 ? <DynamicIslandV5 /> : version === 4 ? <DynamicIslandV4 /> : version === 3 ? <DynamicIslandV3 /> : version === 2 ? <DynamicIslandV2 /> : <DynamicIslandDemo />}
        </CardContent>
      </Card>
    </div>
  )
}
